import { PAPERS, type Paper } from "./svg";
import { FONT_FILES, fontFaceCss, type FontFile } from "./fonts";
import { esc } from "../text";

export const FONT_BASE = "/fonts/";

const cache = new Map<string, Promise<string>>();

function toBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(bin);
}

function fetchBase64(url: string): Promise<string> {
  let p = cache.get(url);
  if (!p) {
    p = fetch(url).then((r) => { if (!r.ok) throw new Error(`${url}: ${r.status}`); return r.arrayBuffer(); }).then(toBase64);
    p.catch(() => cache.delete(url));
    cache.set(url, p);
  }
  return p;
}

const fontUrl = (f: FontFile, ext: "woff2" | "ttf", base = FONT_BASE) => `${base}${f.file}.${ext}`;

/** file name → data: URI, ready for `RenderOptions.fonts`. */
export async function loadFontDataUris(ext: "woff2" | "ttf" = "woff2", base = FONT_BASE): Promise<Record<string, string>> {
  const mime = ext === "woff2" ? "font/woff2" : "font/ttf";
  const entries = await Promise.all(FONT_FILES.map(async (f) => [f.file, `data:${mime};base64,${await fetchBase64(fontUrl(f, ext, base))}`] as const));
  return Object.fromEntries(entries);
}

/** Ensure the SVG carries its own @font-face rules (data URIs) for every family it references. */
export async function embedFonts(svg: string, base = FONT_BASE): Promise<string> {
  if (svg.includes("@font-face")) return svg;
  const used = FONT_FILES.filter((f) => svg.includes(`'${f.family}'`) || svg.includes(`&#39;${f.family}&#39;`));
  if (!used.length) return svg;
  const uris = await loadFontDataUris("woff2", base);
  const css = fontFaceCss(used.map((f) => f.key), (f) => uris[f.file]);
  return svg.includes("<style>") ? svg.replace("<style>", `<style>${css}\n`) : svg.replace(/(<svg[^>]*>)/, `$1<style>${css}</style>`);
}

function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

const svgSize = (svg: string) => {
  const m = /viewBox="0 0 ([\d.]+) ([\d.]+)"/.exec(svg);
  return m ? { w: +m[1], h: +m[2] } : { w: 1123, h: 794 };
};

export async function downloadSvg(svg: string, filename = "raspored.svg", opts: { embedFonts?: boolean } = {}): Promise<void> {
  const out = opts.embedFonts === false ? svg : await embedFonts(svg);
  saveBlob(new Blob([`<?xml version="1.0" encoding="UTF-8"?>\n${out}`], { type: "image/svg+xml;charset=utf-8" }), filename);
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Rasterise to a PNG blob. Default size: 2× the SVG viewBox (phone wallpapers stay at native 1170×2532). */
export interface PngOptions { width?: number; height?: number; paper?: Paper; filename?: string; share?: boolean }

export async function svgToPng(svg: string, opts: PngOptions = {}): Promise<Blob> {
  const full = await embedFonts(svg);
  const { w, h } = svgSize(full);
  const scale = opts.width ? opts.width / w : opts.height ? opts.height / h : opts.paper === "phone" || (w > 1100 && h > 2000) ? 1 : 2;
  const W = Math.round(opts.width ?? w * scale), H = Math.round(opts.height ?? h * scale);
  const img = new Image();
  img.decoding = "sync";
  img.width = W;
  img.height = H;
  img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(full.replace(/(<svg[^>]*?) width="[\d.]+" height="[\d.]+"/, `$1 width="${W}" height="${H}"`))}`;
  await img.decode().catch(() => new Promise((res, rej) => { img.onload = res; img.onerror = rej; }));
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(img, 0, 0, W, H);
  // WebKit sometimes paints SVG-embedded web fonts a frame late; repaint once they are surely in.
  await sleep(120);
  ctx.clearRect(0, 0, W, H);
  ctx.drawImage(img, 0, 0, W, H);
  return new Promise((res, rej) => canvas.toBlob((b) => (b ? res(b) : rej(new Error("PNG encoding failed"))), "image/png"));
}

/**
 * Save as PNG. With `share: true` (useful for phone wallpapers) the Web Share sheet is tried first so iOS users get "Save Image";
 * falls back to a normal download. Resolves to what happened.
 */
export async function downloadPng(svg: string, opts: PngOptions = {}): Promise<"shared" | "downloaded" | "cancelled"> {
  const blob = await svgToPng(svg, opts);
  const filename = opts.filename ?? "raspored.png";
  if (opts.share && typeof navigator !== "undefined" && "canShare" in navigator) {
    const file = new File([blob], filename, { type: "image/png" });
    if (navigator.canShare({ files: [file] })) {
      try { await navigator.share({ files: [file] }); return "shared"; } catch (e) { if ((e as Error).name === "AbortError") return "cancelled"; }
    }
  }
  saveBlob(blob, filename);
  return "downloaded";
}

/** Build the PDF (jspdf + svg2pdf.js are loaded on demand). */
export async function svgToPdf(svg: string, opts: { paper: Paper; title?: string; base?: string }): Promise<Blob> {
  const [{ jsPDF }] = await Promise.all([import("jspdf"), import("svg2pdf.js")]);
  const base = opts.base ?? FONT_BASE;
  const { w, h } = svgSize(svg);
  const pw = w * 0.75, ph = h * 0.75;
  const mm = PAPERS[opts.paper]?.mm;
  const doc = new jsPDF({ unit: "pt", orientation: pw > ph ? "landscape" : "portrait", format: mm && opts.paper.startsWith("a4") ? "a4" : mm && opts.paper.startsWith("a5") ? "a5" : [pw, ph], compress: true });
  const fonts = await Promise.all(FONT_FILES.map(async (f) => [f, await fetchBase64(fontUrl(f, "ttf", base))] as const));
  for (const [f, data] of fonts) {
    doc.addFileToVFS(`${f.file}.ttf`, data);
    doc.addFont(`${f.file}.ttf`, f.family, f.style, f.weight);
  }
  doc.setFont("Schibsted Grotesk", "normal", 400);
  if (opts.title) doc.setProperties({ title: opts.title, creator: "Slušam" });
  const el = new DOMParser().parseFromString(svg.replace(/@font-face\{[^}]*\}/g, ""), "image/svg+xml").documentElement as unknown as SVGSVGElement;
  const host = document.createElement("div");
  host.style.cssText = "position:fixed;left:-99999px;top:0;width:10px;height:10px;overflow:hidden";
  host.appendChild(document.importNode(el, true));
  document.body.appendChild(host);
  try {
    const pageW = doc.internal.pageSize.getWidth(), pageH = doc.internal.pageSize.getHeight();
    await doc.svg(host.firstElementChild as Element, { x: 0, y: 0, width: pageW, height: pageH });
  } finally {
    host.remove();
  }
  return doc.output("blob");
}

export async function downloadPdf(svg: string, opts: { paper: Paper; filename?: string; title?: string }): Promise<void> {
  saveBlob(await svgToPdf(svg, opts), opts.filename ?? "raspored.pdf");
}

/** Open the print tab synchronously inside the click handler (before any await) so popup blockers and iOS allow it. */
export function openPrintWindow(): Window | null {
  return window.open("", "_blank");
}

/**
 * Print via a dedicated document in a new tab (no iframe printing, it is broken on iOS Safari).
 * The tab shows the sheet with Print/Close buttons and auto-opens the print dialog once fonts are ready.
 * Returns the window (null if a popup blocker refused it).
 */
export function printSvg(svg: string, paper: Paper, target?: Window | null, opts: { title?: string; lang?: string; base?: string } = {}): Window | null {
  const win = target ?? window.open("", "_blank");
  if (!win) return null;
  const p = PAPERS[paper] ?? PAPERS["a4-landscape"];
  const [mw, mh] = p.mm ?? [90, (90 * p.h) / p.w];
  const base = new URL(opts.base ?? FONT_BASE, location.href).href;
  const faces = fontFaceCss(FONT_FILES.map((f) => f.key), (f) => fontUrl(f, "woff2", base));
  const lang = opts.lang ?? (/xml:lang="(\w+)"/.exec(svg)?.[1] || "bs");
  const title = opts.title ? esc(opts.title) : /<title>([^<]*)<\/title>/.exec(svg)?.[1] || "Raspored";
  const labels = lang === "en" ? ["Print", "Close"] : ["Štampaj", "Zatvori"];
  const html = `<!doctype html><html lang="${lang}"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>${title}</title><style>
${faces}
@page { size: ${mw}mm ${mh}mm; margin: 0; }
* { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
html, body { margin: 0; padding: 0; }
.sheet svg { display: block; width: 100%; height: 100%; }
@media screen {
  body { background: #57534a; min-height: 100vh; font: 14px/1.3 "Schibsted Grotesk", system-ui, sans-serif; }
  .bar { position: sticky; top: 0; display: flex; gap: 8px; justify-content: center; padding: 12px; background: #1c1a16; z-index: 1; }
  .bar button { font: 600 13px "JetBrains Mono", ui-monospace, monospace; padding: 8px 18px; border-radius: 99px; border: 1.5px solid #f2ede3; background: #f2ede3; color: #1c1a16; cursor: pointer; }
  .bar button + button { background: transparent; color: #f2ede3; }
  .sheet { width: min(96vw, ${mw}mm); aspect-ratio: ${p.w} / ${p.h}; margin: 24px auto; box-shadow: 0 10px 40px rgba(0,0,0,.35); }
}
@media print {
  .bar { display: none !important; }
  html, body { width: ${mw}mm; height: ${mh}mm; overflow: hidden; }
  .sheet { width: ${mw}mm; height: ${mh - 0.4}mm; margin: 0; page-break-after: avoid; break-after: avoid; }
}
</style></head><body><div class="bar"><button type="button" onclick="window.print()">${labels[0]}</button><button type="button" onclick="window.close()">${labels[1]}</button></div><div class="sheet">${svg.replace(/<\?xml[^>]*>/, "")}</div>
<script>(function(){var go=function(){setTimeout(function(){window.focus();window.print();},250)};var f=document.fonts&&document.fonts.ready;if(document.fonts&&document.fonts.load){Promise.all(["400 1em Newsreader","italic 400 1em Newsreader","400 1em 'Schibsted Grotesk'","600 1em 'Schibsted Grotesk'","400 1em 'JetBrains Mono'","600 1em 'JetBrains Mono'"].map(function(s){return document.fonts.load(s).catch(function(){})})).then(function(){return f}).then(go,go)}else if(f){f.then(go,go)}else{window.onload=go}})();</script></body></html>`;
  win.document.open();
  win.document.write(html);
  win.document.close();
  return win;
}
